import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, ArrowRight, Play, CheckCircle2, Flame, Clock, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WebinarCard from '../components/WebinarCard';
import TrendingWebinarCard from '../components/TrendingWebinarCard';
import HeroVideoPreview from '../components/HeroVideoPreview';
import CompanyLogoCarousel from '../components/CompanyLogoCarousel';
import { getWebinars, getWebinarById } from '../services/api';
import { getContinueWatchingList } from '../utils/watchProgress';
import { getWebinarThumbnail } from '../utils/thumbnailHelper';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [allWebinars, setAllWebinars] = useState([]);
  const [topWebinars, setTopWebinars] = useState([]);
  const [featuredWebinars, setFeaturedWebinars] = useState([]);
  const [continueWatching, setContinueWatching] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroWebinar, setHeroWebinar] = useState(null);

  useEffect(() => {
    const fetchWebinars = async () => {
      try {
        setLoading(true);
        
        // Fetch all webinars
        const response = await getWebinars();
        
        if (response.success && response.webinars) {
          const webinars = response.webinars;
          
          // Store all webinars for hero selection
          setAllWebinars(webinars);
          
          // Randomly select a webinar for the hero section (only once when data loads)
          if (webinars.length > 0) {
            const randomIndex = Math.floor(Math.random() * webinars.length);
            const selectedHeroWebinar = webinars[randomIndex];
            // Create a complete copy to ensure all data is from the same object
            setHeroWebinar({
              ...selectedHeroWebinar,
              // Ensure speaker name has a fallback
              speaker: selectedHeroWebinar.speaker || { name: 'Guest Speaker' }
            });
          }
          
          // Get top webinars (trending or most watched)
          const top = webinars
            .filter(w => w.is_trending || w.is_most_watched)
            .slice(0, 4);
          setTopWebinars(top);
          
          // Get featured webinars (rest of them)
          const featured = webinars
            .filter(w => !w.is_trending && !w.is_most_watched)
            .slice(0, 6);
          setFeaturedWebinars(featured);
          
          // Get continue watching list
          const watchingList = getContinueWatchingList();
          if (watchingList.length > 0) {
            // Fetch full webinar details for continue watching items
            const continueWebinars = await Promise.all(
              watchingList.map(async (item) => {
                try {
                  const webinarResponse = await getWebinarById(item.webinarId);
                  if (webinarResponse.success && webinarResponse.webinar) {
                    return {
                      ...webinarResponse.webinar,
                      watchProgress: item,
                    };
                  }
                } catch (err) {
                  console.error('Error fetching continue watching webinar:', err);
                }
                return null;
              })
            );
            setContinueWatching(continueWebinars.filter(Boolean));
          }
        }
      } catch (error) {
        console.error('Error fetching webinars:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWebinars();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/library?search=${encodeURIComponent(searchQuery)}`);
  };

  const categories = [
    { name: 'AI Strategy', count: 8, color: 'bg-blue-100 text-blue-700' },
    { name: 'Data Science', count: 5, color: 'bg-green-100 text-green-700' },
    { name: 'Generative AI', count: 5, color: 'bg-purple-100 text-purple-700' },
    { name: 'Applied AI', count: 5, color: 'bg-orange-100 text-orange-700' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading webinars...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Netflix-Style Hero Section with Video Preview */}
      <HeroVideoPreview webinar={heroWebinar} />

      {/* Search Section - Below Hero with Brand Blue Background */}
      <section className="py-12 bg-[#0c1346]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search for topics, speakers, or programs..."
                className="pl-12 pr-4 h-14 text-lg bg-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
              />
              <Button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 hover:bg-blue-700"
                data-testid="search-button"
              >
                Search
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Continue Watching Section - Only shown if user has watch history */}
      {continueWatching.length > 0 && (
        <section className="py-12 bg-white border-t border-gray-200" data-testid="continue-watching-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <RotateCcw className="h-6 w-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">Continue Watching</h2>
              </div>
              <Button variant="ghost" onClick={() => navigate('/library')} className="text-blue-600 hover:text-blue-700">
                View All
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {continueWatching.map(webinar => {
                const thumbnail = getWebinarThumbnail(webinar);
                const progress = webinar.watchProgress?.percentage || 0;
                return (
                  <Link 
                    key={webinar.webinar_id} 
                    to={`/webinar/${webinar.webinar_id}`}
                    className="group"
                    data-testid={`continue-watching-${webinar.webinar_id}`}
                  >
                    <Card className="overflow-hidden h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={thumbnail}
                          alt={webinar.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=450&fit=crop';
                          }}
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-blue-600 rounded-full p-3">
                            <Play className="h-6 w-6 text-white" fill="white" />
                          </div>
                        </div>
                        {/* Progress bar */}
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-300">
                          <div 
                            className="h-full bg-blue-600 transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors">
                          {webinar.title}
                        </h3>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{webinar.speaker?.name}</span>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{progress}% watched</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Top Webinars Right Now */}
      {topWebinars.length > 0 && (
        <section className="py-16 bg-[#F3F4F6] border-t border-gray-200">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center gap-2 mb-4">
                <Flame className="h-8 w-8 text-orange-500" />
                <h2 className="text-3xl font-bold text-gray-900">Top Webinars Right Now</h2>
              </div>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Don't miss these trending and most-watched sessions from industry experts
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {topWebinars.map(webinar => (
                <TrendingWebinarCard key={webinar.webinar_id} webinar={webinar} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Company Logo Carousel Section */}
      <CompanyLogoCarousel />

      {/* Categories Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Browse by Category</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find webinars tailored to your interests and career goals
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <Card
                key={index}
                className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                onClick={() => navigate('/library')}
              >
                <CardContent className="p-6 text-center">
                  <Badge className={`${category.color} mb-4 text-sm px-3 py-1`}>
                    {category.count} webinars
                  </Badge>
                  <h3 className="font-semibold text-gray-900 text-lg">{category.name}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Webinars */}
      {featuredWebinars.length > 0 && (
        <section className="py-16 bg-gray-50 border-t border-gray-200">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Webinars</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Explore our most popular webinars on Generative AI, Data Science, and AI Strategy
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {featuredWebinars.map(webinar => (
                <WebinarCard key={webinar.webinar_id} webinar={webinar} />
              ))}
            </div>

            <div className="text-center">
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/library')}
              >
                View All Webinars
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-16 bg-[#1B6AE5]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              Access expert knowledge in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-[#1a2ca9]">
                <Search className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-semibold text-white mb-2 text-lg">1. Browse & Discover</h3>
              <p className="text-white/80">
                Search our library of webinars by topic, speaker, or skill level
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-[#1a2ca9]">
                <CheckCircle2 className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-semibold text-white mb-2 text-lg">2. Submit Details</h3>
              <p className="text-white/80">
                Quick registration to unlock full access to webinar recordings
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-[#1a2ca9]">
                <Play className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-semibold text-white mb-2 text-lg">3. Watch & Learn</h3>
              <p className="text-white/80">
                Stream high-quality recordings and explore relevant programs
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
